import { FC, MouseEventHandler, ReactElement, ComponentProps, useCallback, useRef } from 'react';
import { useFragment, graphql, LoadMoreFn } from 'react-relay';
import { OperationType } from 'relay-runtime';
import classnames from 'classnames';

import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import ItemTilesCarousel from 'dibs-product-tile-carousel/exports/ItemTilesCarousel';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';

import { viewAll } from '../../pdpMessages';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { useShowItemLocationBadge } from '../../../shared/hooks/useShowItemLocationBadge';
import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem';
import { FavoritesProvider } from '../../../components/global/FavoriteIcons/FavoritesProvider';

import { PdpSharedProductTileCarousel_viewer$key } from './__generated__/PdpSharedProductTileCarousel_viewer.graphql';
import { PdpSharedProductTileCarousel_items$key } from './__generated__/PdpSharedProductTileCarousel_items.graphql';
import { PdpSharedProductTileCarousel_user$key } from './__generated__/PdpSharedProductTileCarousel_user.graphql';

import dibsCss from 'dibs-css';
import styles from './PdpSharedProductTileCarousel.scss';

const viewerFragment = graphql`
    fragment PdpSharedProductTileCarousel_viewer on Viewer
    @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
        ...ItemTilesCarousel_viewer @arguments(fetchFavorites: false, userId: $userId)
    }
`;
const itemsFragment = graphql`
    fragment PdpSharedProductTileCarousel_items on Item
    @relay(plural: true)
    @argumentDefinitions(
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        priceBookName: { type: "String" }
    ) {
        serviceId
        ...ItemTilesCarousel_items
            @arguments(
                fetchFavorites: false
                userId: $userId
                isTrade: $isTrade
                priceBookName: $priceBookName
            )
        ...FavoritesItem_item
    }
`;

const userFragment = graphql`
    fragment PdpSharedProductTileCarousel_user on User {
        serviceId
        ...ItemTilesCarousel_user
    }
`;

const CAROUSEL_ITEMS_TO_SHOW_RESP = 5;
const CAROUSEL_ITEMS_TO_SHOW_MOBILE = 1.5;

export const PdpSharedProductTileCarousel: FC<{
    viewer: PdpSharedProductTileCarousel_viewer$key;
    items: PdpSharedProductTileCarousel_items$key;
    user: PdpSharedProductTileCarousel_user$key | null | undefined;
    title: ReactElement;
    viewAllLink?: string;
    isViewAllLinkable?: boolean;
    handleViewAllClick?: MouseEventHandler;
    currency: string;
    trackingList: string;
    dataTn?: string;
    carouselCapacity?: number;
    totalResults: number;
    loadNext?: LoadMoreFn<OperationType>;
    onCarouselVisible?: () => void;
    classOverrides?: {
        container?: string;
        title?: string;
        viewAllLink?: string;
    };
    onArrowClick?: ComponentProps<typeof ItemTilesCarousel>['onArrowClick'];
}> = ({
    viewer: viewerRef,
    items: itemsRef,
    user: userRef,
    title,
    viewAllLink,
    isViewAllLinkable = true,
    handleViewAllClick = () => {},
    currency,
    trackingList,
    dataTn,
    carouselCapacity = 10,
    totalResults,
    loadNext,
    onCarouselVisible = () => {},
    classOverrides = {},
    onArrowClick,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const items = useFragment(itemsFragment, itemsRef);
    const user = useFragment(userFragment, userRef);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const { isMobile, isItemTileShorterHeight } = useServerVarsContext();
    const showItemLocationBadge = useShowItemLocationBadge();
    const isClient = useClientState();

    const onVisibilityChange: ComponentProps<typeof VisibilityTracker>['onVisibilityChange'] =
        useCallback(
            ({ isVisible }) => {
                if (isVisible) {
                    onCarouselVisible();
                }
            },
            [onCarouselVisible]
        );

    if (!items.length) {
        return null;
    }

    return (
        <div
            ref={wrapperRef}
            className={
                classOverrides.container ||
                classnames({
                    [styles.container]: isMobile,
                    [dibsCss.pSmall]: isMobile,
                    [dibsCss.mbLarger]: !isMobile,
                })
            }
            data-tn={dataTn}
        >
            <VisibilityTracker
                elementRef={wrapperRef}
                onVisibilityChange={onVisibilityChange}
                observerOptions={{ threshold: 0.5 }}
            />
            {title && (
                <HeadingLevel>
                    {Heading => (
                        <Heading
                            className={
                                classOverrides.title ||
                                classnames({
                                    [dibsCss.sassyFontHeaderMedium]: isMobile,
                                    [dibsCss.sassyFontHeaderLarge]: !isMobile,
                                    [dibsCss.m0]: !isMobile || viewAllLink,
                                    [dibsCss.mbSmall]: isMobile && !viewAllLink,
                                    [dibsCss.mt0]: isMobile && !viewAllLink,
                                })
                            }
                        >
                            {title}
                        </Heading>
                    )}
                </HeadingLevel>
            )}
            {viewAllLink && (
                <SeoLink
                    className={
                        classOverrides.viewAllLink ||
                        classnames(dibsCss.sassyFontBody, dibsCss.block, {
                            [dibsCss.mbSmall]: isMobile,
                            [dibsCss.mtXsmall]: !isMobile,
                        })
                    }
                    onClick={handleViewAllClick}
                    linkData={{
                        path: viewAllLink,
                        isLinkable: isViewAllLinkable,
                    }}
                    dataTn={`${dataTn || 'view-all'}-link`}
                >
                    {viewAll}
                </SeoLink>
            )}

            <FavoritesProvider
                userId={user?.serviceId}
                itemIds={items.map(({ serviceId }) => serviceId || '')}
                location={trackingList as $TSFixMe}
                disable={!isClient}
                fetchFolder={!isMobile}
            >
                {({ props: favorites }) => (
                    <ItemTilesCarousel
                        viewer={viewer}
                        user={user}
                        items={items}
                        currency={currency}
                        authModalLoader={authModalLoader}
                        carouselCapacity={carouselCapacity}
                        totalResults={totalResults}
                        loadNext={loadNext}
                        trackingList={trackingList}
                        itemsToShow={
                            isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP
                        }
                        tileImageSize={isMobile ? 'imageLarge' : 'imageXSmall'}
                        isInfinite={!isMobile}
                        showArrows={!isMobile}
                        showQuickView={!isMobile}
                        hideImagePadding={!isMobile}
                        gapSize={isMobile ? 'small' : 'large'}
                        fullWidth={!!isMobile}
                        showItemLocationBadge={showItemLocationBadge}
                        isItemTileShorterHeight={isItemTileShorterHeight || false}
                        onArrowClick={onArrowClick}
                        favoritesComponentOverrides={items.map(
                            item =>
                                ({ isFolderVisible, ...favoritesItemProps }) =>
                                    (
                                        <FavoritesItem
                                            wrapperClass={classnames(
                                                dibsCss.zBump,
                                                dibsCss.absolute,
                                                dibsCss.top0,
                                                dibsCss.right0
                                            )}
                                            heartIconWrapperClass={classnames(
                                                styles.icon,
                                                dibsCss.relative
                                            )}
                                            folderIconWrapperClass={classnames(
                                                styles.icon,
                                                dibsCss.relative
                                            )}
                                            showFolderPlusIcon
                                            item={item}
                                            {...favorites}
                                            {...favoritesItemProps}
                                            fetchFolder={!!isFolderVisible && !isMobile}
                                        />
                                    )
                        )}
                    />
                )}
            </FavoritesProvider>
        </div>
    );
};
